import {
  getStripeChargeAmountQuantum,
  getStripeMinorUnitMultiplier,
} from "../utils/stripeCurrencies";

export const ORGANIZER_PAYOUT_TERMS_VERSION =
  "rauxa-organizer-payout-terms-v3";

export const FEE_POLICY_VERSION = "rauxa-stripe-v3";
export const PRICING_DISCLOSURE_VERSION =
  "rauxa-registration-pricing-v1";
export const TAX_POLICY_VERSION = "rauxa-jurisdiction-tax-v1";
export const TAX_INTEGRATION_VERSION = "stripe-tax-tickets-v1";
export const TAX_DISCLOSURE_VERSION = "rauxa-registration-tax-v1";
export const ORGANIZER_COMMISSION_BPS = 1500;
export const RACER_SERVICE_FEE_BPS = 400;
export const ORGANIZER_SHARE_BPS = 10000 - ORGANIZER_COMMISSION_BPS;

/** @deprecated Use ORGANIZER_COMMISSION_BPS. */
export const CORSA_PLATFORM_FEE_BPS = ORGANIZER_COMMISSION_BPS;

type OrganizerPayoutTermsRecord = {
  acceptedOrganizerPayoutTerms?: boolean | null;
  organizerPayoutTermsVersion?: string | null;
};

export type OrganizerPayoutTermsStatus =
  | "accepted"
  | "updateRequired"
  | "acceptanceRequired";

const basisPointsToRatio = (basisPoints: number): number => basisPoints / 10000;

const getMinorUnitMultiplier = (currency: string): number =>
  getStripeMinorUnitMultiplier(currency);

const minorUnitsToMajorUnits = (amount: number, multiplier: number): number =>
  amount / multiplier;

export const hasAcceptedCurrentOrganizerPayoutTerms = (
  value: OrganizerPayoutTermsRecord | null | undefined
): boolean =>
  Boolean(
    value?.acceptedOrganizerPayoutTerms &&
      value?.organizerPayoutTermsVersion === ORGANIZER_PAYOUT_TERMS_VERSION
  );

export const getOrganizerPayoutTermsStatus = (
  value: OrganizerPayoutTermsRecord | null | undefined
): OrganizerPayoutTermsStatus => {
  if (hasAcceptedCurrentOrganizerPayoutTerms(value)) {
    return "accepted";
  }

  if (value?.acceptedOrganizerPayoutTerms) {
    return "updateRequired";
  }

  return "acceptanceRequired";
};

export const getOrganizerPayoutTermsStatusLabel = (
  status: OrganizerPayoutTermsStatus
): string => {
  if (status === "accepted") {
    return "Accepted";
  }

  if (status === "updateRequired") {
    return "Update Required";
  }

  return "Acceptance Required";
};

export const calculateOrganizerPayoutPreview = (
  entryFee: number,
  currency = "USD"
) => {
  const normalizedEntryFee = Number.isFinite(entryFee) && entryFee > 0 ? entryFee : 0;
  const minorUnitMultiplier = getMinorUnitMultiplier(currency);
  const amountQuantum = getStripeChargeAmountQuantum(currency);
  const entryFeeMinor = Math.round(normalizedEntryFee * minorUnitMultiplier);
  const organizerCommissionMinor =
    Math.round(
      (entryFeeMinor * basisPointsToRatio(ORGANIZER_COMMISSION_BPS)) /
        amountQuantum
    ) * amountQuantum;
  const organizerShareMinor = Math.max(
    entryFeeMinor - organizerCommissionMinor,
    0
  );
  const racerServiceFeeMinor =
    Math.round(
      (entryFeeMinor * basisPointsToRatio(RACER_SERVICE_FEE_BPS)) /
        amountQuantum
    ) * amountQuantum;
  const racerTotalMinor = entryFeeMinor + racerServiceFeeMinor;
  const platformRetainedMinor =
    organizerCommissionMinor + racerServiceFeeMinor;

  return {
    entryFee: minorUnitsToMajorUnits(entryFeeMinor, minorUnitMultiplier),
    organizerCommission: minorUnitsToMajorUnits(
      organizerCommissionMinor,
      minorUnitMultiplier
    ),
    /** @deprecated Compatibility alias for organizerCommission. */
    corsaFee: minorUnitsToMajorUnits(
      organizerCommissionMinor,
      minorUnitMultiplier
    ),
    organizerShare: minorUnitsToMajorUnits(
      organizerShareMinor,
      minorUnitMultiplier
    ),
    racerServiceFee: minorUnitsToMajorUnits(
      racerServiceFeeMinor,
      minorUnitMultiplier
    ),
    racerTotal: minorUnitsToMajorUnits(racerTotalMinor, minorUnitMultiplier),
    platformRetained: minorUnitsToMajorUnits(
      platformRetainedMinor,
      minorUnitMultiplier
    ),
  };
};
